/**
 * Applies the checked swatch (if any) to a given product card by invoking its swatch change handler.
 * This encapsulates the shared logic so it can be reused by other initialisation flows.
 *
 * @param {HTMLElement} card - The <product-card> element to sync.
 */
function applyCheckedSwatchToProductCard(card) {
  const checkedSwatch = card.querySelector('.opt-btn:checked');
  if (checkedSwatch && typeof card.handleSwatchChange === 'function') {
    card.handleSwatchChange({ target: checkedSwatch });
  }
}

/**
 * Ensures all product cards display the correct variant image if a swatch is checked (e.g., after browser back navigation).
 */
function syncProductCardVariantImages() {
  document.querySelectorAll('product-card').forEach((card) => {
    applyCheckedSwatchToProductCard(card);
  });
}

// On browser history navigation (back/forward), re-sync product card images.
// The theme dispatches this event on `document` (non-bubbling), so listen there
// to match the dispatch target and the theme's other consumer (custom-pagination.js).
document.addEventListener('on:bfcache:load-restore', syncProductCardVariantImages);

// Optionally, also re-sync after Shopify section reloads (AJAX grid reload)
document.addEventListener('shopify:section:load', syncProductCardVariantImages);
/**
 * Polyfills :focus-visible for non supporting browsers (Safari < 15.4).
 */
function focusVisiblePolyfill() {
  const navKeys = [
    'Tab',
    'ArrowUp',
    'ArrowDown',
    'ArrowLeft',
    'ArrowRight',
    'Enter',
    'Space',
    'Escape',
    'Home',
    'End',
    'PageUp',
    'PageDown',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (evt) => {
    if (navKeys.includes(evt.code)) mouseClick = false;
  });

  window.addEventListener('mousedown', () => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('is-focused');
      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('is-focused');
    },
    true,
  );
}

// Add polyfill if :focus-visible is not supported.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

/**
 * Creates a 'mediaMatches' object from the media queries specified in the theme,
 * and adds listeners for each media query. If a breakpoint is crossed, the mediaMatches
 * values are updated and a 'on:breakpoint-change' event is dispatched.
 */
(() => {
  const { mediaQueries } = theme;
  if (!mediaQueries) return;

  const mqKeys = Object.keys(mediaQueries);
  const mqLists = {};
  theme.mediaMatches = {};

  /**
   * Handles a media query (breakpoint) change.
   */
  const handleMqChange = () => {
    const newMatches = mqKeys.reduce((acc, media) => {
      acc[media] = !!(mqLists[media] && mqLists[media].matches);
      return acc;
    }, {});

    // Update mediaMatches values after breakpoint change.
    Object.keys(newMatches).forEach((key) => {
      theme.mediaMatches[key] = newMatches[key];
    });

    window.dispatchEvent(new CustomEvent('on:breakpoint-change'));
  };

  mqKeys.forEach((mq) => {
    // Create mqList object for each media query.
    mqLists[mq] = window.matchMedia(mediaQueries[mq]);

    // Get initial matches for each query.
    theme.mediaMatches[mq] = mqLists[mq].matches;

    // Add an event listener to each query.
    try {
      mqLists[mq].addEventListener('change', handleMqChange);
    } catch (err1) {
      // Fallback for legacy browsers (Safari < 14).
      mqLists[mq].addListener(handleMqChange);
    }
  });
})();

/**
 * Returns a function that as long as it continues to be invoked, won't be triggered.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Delay (in milliseconds).
 * @returns {Function}
 */
function debounce(fn, wait = 300) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

/**
 * Sets a 'viewport-height' custom property on the root element.
 */
function setViewportHeight() {
  document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
}

/**
 * Sets a 'header-height' custom property on the root element.
 */
function setHeaderHeight() {
  const header = document.getElementById('shopify-section-header');
  if (!header) return;
  let height = header.offsetHeight;

  // Add announcement bar height (if shown).
  const announcement = document.getElementById('shopify-section-announcement');
  if (announcement) height += announcement.offsetHeight;

  document.documentElement.style.setProperty('--header-height', `${height}px`);
}

/**
 * Sets a 'scrollbar-width' custom property on the root element.
 */
function setScrollbarWidth() {
  document.documentElement.style.setProperty(
    '--scrollbar-width',
    `${window.innerWidth - document.documentElement.clientWidth}px`,
  );
}

/**
 * Sets the dimension variables.
 */
function setDimensionVariables() {
  setViewportHeight();
  setHeaderHeight();
  setScrollbarWidth();
}

// Set the dimension variables.
setDimensionVariables();

// Update the dimension variables if viewport resized.
window.addEventListener('resize', debounce(setDimensionVariables, 50));

// iOS alters screen width without resize event, if unexpectedly wide content is found
setTimeout(setViewportHeight, 3000);

/**
 * Checks if a lazy load image has alternate <source> elements and copies the
 * 'data-src' and 'data-srcset' attributes to 'src' and 'srcset' accordingly.
 * @param {Element} img - Image element.
 */
function setImageSources(img) {
  const setImageAttr = (el) => {
    if (el.dataset.src && !el.src) {
      el.src = el.dataset.src;
    }

    if (el.dataset.srcset && !el.srcset) {
      el.srcset = el.dataset.srcset;
    }
  };

  if (img.parentNode.tagName === 'PICTURE') {
    Array.from(img.parentNode.children).forEach((el) => {
      setImageAttr(el);
    });
  } else {
    setImageAttr(img);
  }
}

/**
 * Initialises lazy load images.
 */
function initLazyImages() {
  if ('loading' in HTMLImageElement.prototype === false && 'IntersectionObserver' in window) {
    // If native lazyload not supported but IntersectionObserver supported (Safari).
    const io = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const img = entry.target;
            setImageSources(img);
            observer.unobserve(img);
          }
        });
      },
      { rootMargin: '0px 0px 500px 0px' },
    );

    document.querySelectorAll('[loading="lazy"]').forEach((img) => {
      io.observe(img);
    });
  } else {
    // If native lazy load supported or IntersectionObserver not supported (legacy browsers).
    document.querySelectorAll('[loading="lazy"]').forEach((img) => {
      setImageSources(img);
    });
  }
}

/**
 * Adds an observer to initialise a script when an element is scrolled into view.
 * @param {Element} element - Element to observe.
 * @param {Function} callback - Function to call when element is scrolled into view.
 * @param {number} [threshold=500] - Distance from viewport (in pixels) to trigger init.
 */
function initLazyScript(element, callback, threshold = 500) {
  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (typeof callback === 'function') {
              callback();
              observer.unobserve(entry.target);
            }
          }
        });
      },
      { rootMargin: `0px 0px ${threshold}px 0px` },
    );

    io.observe(element);
  } else {
    callback();
  }
}

/**
 * Pauses all media (videos/models) within an element.
 * @param {Element} [el=document] - Element to pause media within.
 */
function pauseAllMedia(el = document) {
  el.querySelectorAll('.js-youtube, .js-vimeo, video').forEach((video) => {
    const component = video.closest('video-component');
    if (component && component.dataset.background === 'true') return;

    if (video.matches('.js-youtube')) {
      video.contentWindow.postMessage('{ "event": "command", "func": "pauseVideo", "args": "" }', '*');
    } else if (video.matches('.js-vimeo')) {
      video.contentWindow.postMessage('{ "method": "pause" }', '*');
    } else {
      video.pause();
    }
  });

  el.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

class DeferredMedia extends HTMLElement {
  constructor() {
    super();

    const loadBtn = this.querySelector('.js-load-media');
    if (loadBtn) {
      loadBtn.addEventListener('click', this.loadContent.bind(this));
    } else {
      this.addObserver();
    }
  }

  /**
   * Adds an Intersection Observer to load the content when viewport scroll is near
   */
  addObserver() {
    if ('IntersectionObserver' in window === false) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.loadContent(false, false, 'observer');
            observer.unobserve(this);
          }
        });
      },
      { rootMargin: '0px 0px 1000px 0px' },
    );

    observer.observe(this);
  }

  /**
   * Loads the deferred media.
   * @param {boolean} [focus=true] - Focus the deferred media element after loading.
   * @param {boolean} [pause=true] - Whether to pause all media after loading.
   * @param {string} [loadTrigger='click'] - The action that caused the deferred content to load.
   */
  loadContent(focus = true, pause = true, loadTrigger = 'click') {
    if (pause) pauseAllMedia();
    if (this.getAttribute('loaded') !== null) return;

    this.loadTrigger = loadTrigger;
    const content = this.querySelector('template').content.firstElementChild.cloneNode(true);
    this.appendChild(content);
    this.setAttribute('loaded', '');

    const deferredEl = this.querySelector('video, model-viewer, iframe');
    if (deferredEl && focus) deferredEl.focus();
  }
}

customElements.define('deferred-media', DeferredMedia);

class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.disclosure = this.querySelector('details');
    this.toggle = this.querySelector('summary');
    this.panel = this.toggle.nextElementSibling;
    this.init();
  }

  init() {
    // Check if the content element has a CSS transition.
    if (window.getComputedStyle(this.panel).transitionDuration !== '0s') {
      this.toggle.addEventListener('click', this.handleToggle.bind(this));
      this.disclosure.addEventListener('transitionend', this.handleTransitionEnd.bind(this));
      // Sync aria-expanded and is-open with initial open state (e.g., rendered with open attribute).
      const initialOpen = this.disclosure.open;
      this.toggle.setAttribute('aria-expanded', String(initialOpen));
      this.disclosure.classList.toggle('is-open', initialOpen);
    } else {
      // No CSS transition — native <details> toggle handles open/close,
      // so sync aria-expanded and is-open class via the toggle event.
      this.disclosure.addEventListener('toggle', () => {
        const isOpen = this.disclosure.open;
        this.toggle.setAttribute('aria-expanded', String(isOpen));
        this.disclosure.classList.toggle('is-open', isOpen);
      });
      // Ensure aria-expanded and is-open match the initial open state when instantiated.
      const initialOpen = this.disclosure.open;
      this.toggle.setAttribute('aria-expanded', String(initialOpen));
      this.disclosure.classList.toggle('is-open', initialOpen);
    }
  }

  /**
   * Handles 'click' events on the summary element.
   * @param {object} evt - Event object.
   */
  handleToggle(evt) {
    evt.preventDefault();

    if (!this.disclosure.open) {
      this.open();
    } else {
      this.close();
    }
  }

  /**
   * Handles 'transitionend' events on the details element.
   * @param {object} evt - Event object.
   */
  handleTransitionEnd(evt) {
    if (evt.target !== this.panel) return;

    if (this.disclosure.classList.contains('is-closing')) {
      this.disclosure.classList.remove('is-closing');
      this.disclosure.open = false;
    }

    this.panel.removeAttribute('style');
  }

  /**
   * Adds inline 'height' style to the content element, to trigger open transition.
   */
  addContentHeight() {
    this.panel.style.height = `${this.panel.scrollHeight}px`;
  }

  /**
   * Opens the details element.
   */
  open() {
    // Set content 'height' to zero before opening the details element.
    this.panel.style.height = '0';

    // Open the details element
    this.disclosure.open = true;
    this.disclosure.classList.add('is-open');
    this.toggle.setAttribute('aria-expanded', 'true');

    // Set content 'height' to its scroll height, to enable CSS transition.
    this.addContentHeight();
  }

  /**
   * Closes the details element.
   */
  close() {
    // Set content height to its scroll height, to enable transition to zero.
    this.addContentHeight();

    // Add class to enable styling of content or toggle icon before or during close transition.
    this.disclosure.classList.add('is-closing');
    this.disclosure.classList.remove('is-open');
    this.toggle.setAttribute('aria-expanded', 'false');

    // Set content height to zero to trigger the transition.
    // Slight delay required to allow scroll height to be applied before changing to '0'.
    setTimeout(() => {
      this.panel.style.height = '0';
    });
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

/**
 * Accessible accordion component using button + aria-expanded pattern.
 * Replaces the details/summary pattern in the FAQ section to ensure
 * consistent screen reader announcements of expanded/collapsed state.
 */
class AccordionItem extends HTMLElement {
  constructor() {
    super();
    this.disclosure = this.querySelector('.disclosure');
    this.toggle = this.querySelector('button[aria-expanded]');
    this.panel = this.querySelector('[role="region"]');
    this.init();
  }

  init() {
    // Measure transition before hiding — hidden elements report 0s.
    this.hasTransition = window.getComputedStyle(this.panel).transitionDuration !== '0s';

    // Hide panel via JS so no-JS users still see all content.
    this.panel.hidden = true;

    this.toggle.addEventListener('click', this.handleToggle.bind(this));
    if (this.hasTransition) {
      this.panel.addEventListener('transitionend', this.handleTransitionEnd.bind(this));
    }
  }

  handleToggle() {
    if (this.toggle.getAttribute('aria-expanded') === 'false') {
      this.open();
    } else {
      this.close();
    }
  }

  handleTransitionEnd(evt) {
    if (evt.target !== this.panel) return;
    if (this.disclosure.classList.contains('is-closing')) {
      this.disclosure.classList.remove('is-closing');
      this.disclosure.classList.remove('is-open');
      this.panel.hidden = true;
    }
    this.panel.removeAttribute('style');
  }

  addContentHeight() {
    this.panel.style.height = `${this.panel.scrollHeight}px`;
  }

  open() {
    this.panel.style.height = '0';
    this.panel.hidden = false;
    this.disclosure.classList.add('is-open');
    this.toggle.setAttribute('aria-expanded', 'true');
    this.addContentHeight();
  }

  close() {
    this.toggle.setAttribute('aria-expanded', 'false');
    if (this.hasTransition) {
      this.addContentHeight();
      this.disclosure.classList.add('is-closing');
      setTimeout(() => {
        this.panel.style.height = '0';
      });
    } else {
      this.disclosure.classList.remove('is-open');
      this.panel.hidden = true;
    }
  }
}

customElements.define('accordion-item', AccordionItem);

const trapFocusHandlers = {};

/**
 * Removes focus trap event listeners and optionally focuses an element.
 * @param {Element} [elementToFocus=null] - Element to focus when trap is removed.
 */
function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

/**
 * Traps focus within a container, e.g. modal or side drawer.
 * @param {Element} container - Container element to trap focus within.
 * @param {Element} [elementToFocus=container] - Initial element to focus when trap is applied.
 */
function trapFocus(container, elementToFocus = container) {
  removeTrapFocus();

  const focusableSelector =
    'summary, a[href], area[href], button:not([disabled]), input:not([type=hidden]):not([disabled]), select:not([disabled]), textarea:not([disabled]), object, iframe, audio[controls], video[controls], [tabindex]:not([tabindex^="-"])';

  // csfix-7: Query focusable elements live on each Tab rather than caching them
  // at open time. The Quick Add / Choose Options drawer fetches its product form
  // asynchronously, so at open time the drawer is empty and a cached first/last
  // element would be stale — letting focus tab out to the page behind. Filter to
  // visible elements (drops the hidden back button). Unlike an offsetParent check,
  // this still counts position:fixed controls (e.g. the sticky buy buttons), which
  // have a null offsetParent even when visible.
  const isVisible = (el) => !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
  const getFocusableEls = () => Array.from(container.querySelectorAll(focusableSelector)).filter(isVisible);

  trapFocusHandlers.focusin = (evt) => {
    if (!container.contains(evt.target)) return;
    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = () => {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = (evt) => {
    if (evt.code !== 'Tab') return;

    const focusableEls = getFocusableEls();
    if (focusableEls.length === 0) return;

    const firstEl = focusableEls[0];
    const lastEl = focusableEls[focusableEls.length - 1];

    // If tab pressed on last focusable element, focus the first element.
    if (evt.target === lastEl && !evt.shiftKey) {
      evt.preventDefault();
      firstEl.focus();
    }

    //  If shift + tab pressed on the first focusable element, focus the last element.
    if ((evt.target === container || evt.target === firstEl) && evt.shiftKey) {
      evt.preventDefault();
      lastEl.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  (elementToFocus || container).focus();
}

class Modal extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', this.handleClick.bind(this));
  }

  /**
   * Handles 'click' events on the modal.
   * @param {object} evt - Event object.
   */
  handleClick(evt) {
    if (evt.target !== this && !evt.target.matches('.js-close-modal')) return;
    this.close();
  }

  /**
   * Opens the modal.
   * @param {Element} opener - Modal opener element.
   */
  open(opener) {
    // Prevent page behind from scrolling when side drawer is open
    this.scrollY = window.scrollY;
    document.body.classList.add('fixed');
    document.body.style.top = `-${this.scrollY}px`;

    this.setAttribute('open', '');
    this.openedBy = opener;

    trapFocus(this);
    window.pauseAllMedia();

    // Add event handler (so the bound event listener can be removed).
    this.keyupHandler = (evt) => evt.key === 'Escape' && this.close();

    // Add event listener (for while modal is open).
    this.addEventListener('keyup', this.keyupHandler);
  }

  /**
   * Closes the modal.
   */
  close() {
    // Restore page position and scroll behaviour.
    document.body.style.top = '';
    document.body.classList.remove('fixed');
    window.scrollTo(0, this.scrollY);

    this.removeAttribute('open');

    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();

    // Remove event listener added on modal opening.
    this.removeEventListener('keyup', this.keyupHandler);
  }
}

customElements.define('modal-dialog', Modal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');
    if (!button) return;

    button.addEventListener('click', () => {
      const modal = document.getElementById(this.dataset.modal);
      if (modal) modal.open(button);
    });
  }
}

customElements.define('modal-opener', ModalOpener);

class ProductCard extends HTMLElement {
  constructor() {
    super();
    window.initLazyScript(this, this.init.bind(this));
  }

  init() {
    this.images = this.querySelectorAll('.card__main-image');
    this.hoverImages = this.querySelectorAll('.card__hover-image');
    this.links = this.querySelectorAll('.js-prod-link');
    this.quickAddBtn = this.querySelector('.js-quick-add');

    // Store the product page URL separately (for quick-add functionality)
    if (this.quickAddBtn) {
      this.productPageUrl = this.quickAddBtn.dataset.productUrl;
    } else if (this.links.length) {
      // Extract the base product URL without variant parameter
      const linkHref = this.links[0].href;
      this.productPageUrl = linkHref.split('?')[0];
    }

    // Store the initial link URL (might be customizer URL)
    if (this.links.length) {
      this.productUrl = this.links[0].href;
    }

    // WEB-17657: Check if default variant has a hover image, remove class if not
    if (this.hoverImages.length) {
      const visibleHoverImage = Array.from(this.hoverImages).find((img) => !img.hidden);
      const cardMedia = this.querySelector('.card__media');

      if (!visibleHoverImage && cardMedia) {
        cardMedia.classList.remove('has-hover-image');
      }
    }

    this.addEventListener('change', this.handleSwatchChange.bind(this));

    // Sync images if a swatch is already checked (e.g., after browser back navigation)
    applyCheckedSwatchToProductCard(this);

    // WEB-18274: Dynamic swatch overflow — show as many as fit in one row
    this.swatchList = this.querySelector('.card__swatches');
    if (this.swatchList) {
      this.swatchOverflow = this.swatchList.querySelector('.card__swatches-overflow');
      this.allSwatches = Array.from(this.swatchList.querySelectorAll('.swatch:not([data-offline])'));

      if (this.allSwatches.length && this.swatchOverflow) {
        this.updateSwatchOverflow();
        this.swatchRO = new ResizeObserver(() => this.updateSwatchOverflow());
        this.swatchRO.observe(this.swatchList);
      }
    }
  }

  disconnectedCallback() {
    this.swatchRO?.disconnect();
  }

  /**
   * WEB-18274: Calculates how many swatches fit in one row and hides the rest,
   * showing a "+X" overflow indicator when needed.
   */
  updateSwatchOverflow() {
    const swatches = this.allSwatches;
    const overflow = this.swatchOverflow;

    // Reset: show all swatches, restore focusability, hide overflow
    swatches.forEach((s) => {
      s.hidden = false;
      s.querySelector('input')?.removeAttribute('tabindex');
    });
    overflow.hidden = true;

    if (swatches.length < 2) return;

    const containerWidth = this.swatchList.clientWidth;
    if (!containerWidth) return;

    // Measure the step between consecutive swatches (accounts for all margins/gaps)
    const step = swatches[1].getBoundingClientRect().left - swatches[0].getBoundingClientRect().left;
    if (step <= 0) return;

    const totalFit = Math.floor(containerWidth / step);

    // If all swatches fit, we're done
    if (swatches.length <= totalFit) return;

    // Measure overflow indicator width
    overflow.hidden = false;
    overflow.textContent = `+${swatches.length}`;
    const overflowWidth = overflow.offsetWidth + 4; // 4px breathing room

    // How many swatch positions fit alongside the overflow indicator?
    const visibleCount = Math.max(1, Math.floor((containerWidth - overflowWidth) / step));

    // Build the set of visible indices, ensuring the checked swatch is always included
    const visible = new Set(Array.from({ length: visibleCount }, (_, i) => i));
    const selectedIndex = swatches.findIndex((s) => s.querySelector('input')?.checked);
    if (selectedIndex >= 0 && !visible.has(selectedIndex)) {
      visible.delete(visibleCount - 1);
      visible.add(selectedIndex);
    }

    // Hide overflow swatches (but always keep the checked swatch visible)
    swatches.forEach((s, i) => {
      const isVisible = visible.has(i);
      s.hidden = !isVisible;
      const input = s.querySelector('input');
      if (!isVisible) {
        input?.setAttribute('tabindex', '-1');
      } else {
        input?.removeAttribute('tabindex');
      }
    });

    const hiddenCount = swatches.length - visibleCount;
    if (hiddenCount > 0) {
      overflow.textContent = `+${hiddenCount}`;
      overflow.hidden = false;
      overflow.setAttribute('aria-hidden', 'false');
    } else {
      overflow.hidden = true;
      overflow.setAttribute('aria-hidden', 'true');
    }
  }

  /**
   * Handles 'change' events in the product card swatches.
   * @param {object} evt - Event object.
   */
  handleSwatchChange(evt) {
    if (!evt.target.matches('.opt-btn')) return;

    // Swap current card image to selected variant image.
    if (evt.target.dataset.mediaId) {
      const variantMedia = this.querySelector(`[data-media-id="${evt.target.dataset.mediaId}"]`);

      if (variantMedia) {
        this.images.forEach((image) => {
          image.hidden = true;
        });
        variantMedia.hidden = false;
      }
    }

    // WEB-17657: Swap hover image to selected variant's hover image
    if (evt.target.dataset.variantId && this.hoverImages.length) {
      const variantHoverImage = this.querySelector(
        `[data-hover-media-id][data-variant-id="${evt.target.dataset.variantId}"]`,
      );

      // Hide all hover images first
      this.hoverImages.forEach((hoverImage) => {
        hoverImage.hidden = true;
      });

      const cardMedia = this.querySelector('.card__media');

      // Show the selected variant's hover image if it exists
      if (variantHoverImage) {
        variantHoverImage.hidden = false;
        // Add has-hover-image class when variant has hover image
        cardMedia?.classList.add('has-hover-image');
      } else {
        // Remove has-hover-image class when variant doesn't have hover image
        cardMedia?.classList.remove('has-hover-image');
      }
    }

    // Check if we're in customizer mode and variant is spectrum-enabled
    const customizerMode = this.dataset.customizerMode === 'true';
    const spectrumEnabled = evt.target.dataset.spectrumEnabled === 'true';

    let url;
    if (customizerMode && spectrumEnabled && evt.target.dataset.variantSku) {
      // Build customizer URL from the base URL set by Liquid (not derived from link href)
      const customizerBaseUrl = this.dataset.customizerBaseUrl;
      url = `${customizerBaseUrl}?pid=${evt.target.dataset.variantSku}`;
    } else {
      // Standard product page variant URL (also used when variant isn't spectrum-enabled)
      const pdpUrl = evt.target.dataset.productPageUrl || this.productPageUrl;
      url = `${pdpUrl}?variant=${evt.target.dataset.variantId}`;
    }

    // Update link hrefs to url of selected variant.
    this.links.forEach((link) => {
      link.href = url;
    });

    // Update the Quick Add button data.
    if (this.quickAddBtn) {
      this.quickAddBtn.dataset.selectedColor = evt.target.value;
    }

    // WEB-18274: Re-evaluate visible swatches so the checked one stays in view
    if (this.swatchOverflow) {
      this.updateSwatchOverflow();
    }
  }
}

customElements.define('product-card', ProductCard);

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('.qty-input__input');
    this.currentQty = this.input.value;
    this.changeEvent = new Event('change', { bubbles: true });

    this.addEventListener('click', this.handleClick.bind(this));
    this.input.addEventListener('keydown', this.handleKeydown.bind(this));
  }

  /**
   * Handles 'click' events on the quantity input element.
   * @param {object} evt - Event object.
   */
  handleClick(evt) {
    if (!evt.target.matches('.qty-input__btn')) return;
    evt.preventDefault();

    if (evt.target.name === 'plus') {
      this.input.stepUp();
    } else {
      this.input.stepDown();
    }

    if (this.input.value !== this.currentQty) {
      this.input.dispatchEvent(this.changeEvent);
      this.currentQty = this.input.value;
    }
  }

  /**
   * Handles 'keydown' events on the input field.
   * @param {object} evt - Event object.
   */
  handleKeydown(evt) {
    if (evt.key !== 'Enter') return;
    evt.preventDefault();

    if (this.input.value !== this.currentQty) {
      this.input.blur();
      this.input.focus();
      this.currentQty = this.input.value;
    }
  }
}

customElements.define('quantity-input', QuantityInput);

class SideDrawer extends HTMLElement {
  constructor() {
    super();
    this.overlay = document.querySelector('.js-overlay');
  }

  /**
   * Handles a 'click' event on the drawer.
   * @param {object} evt - Event object.
   */
  handleClick(evt) {
    if (evt.target.matches('.js-close-drawer') || evt.target === this.overlay) {
      this.close();
    }
  }

  /**
   * Opens the drawer.
   * @param {Element} [opener] - Element that triggered opening of the drawer.
   * @param {Element} [elementToFocus] - Element to focus after drawer opened.
   * @param {Function} [callback] - Callback function to trigger after the open has completed
   */
  open(opener, elementToFocus, callback) {
    this.dispatchEvent(
      new CustomEvent(`on:${this.dataset.name}:before-open`, {
        bubbles: true,
      }),
    );

    // Prevent page behind from scrolling when side drawer is open.
    this.scrollY = window.scrollY;
    document.body.classList.add('fixed');
    document.body.style.top = `-${this.scrollY}px`;
    document.documentElement.style.height = '100vh';

    this.overlay.classList.add('is-visible');
    this.setAttribute('open', '');
    this.setAttribute('aria-hidden', 'false');
    this.opener = opener;

    trapFocus(this, elementToFocus);

    // Create event handler variables (so the bound event listeners can be removed).
    this.clickHandler = this.clickHandler || this.handleClick.bind(this);
    this.keyupHandler = (evt) => {
      if (evt.key !== 'Escape' || evt.target.closest('.cart-drawer-popup')) return;
      this.close();
    };

    // Add event listeners (for while drawer is open).
    this.addEventListener('click', this.clickHandler);
    this.addEventListener('keyup', this.keyupHandler);
    this.overlay.addEventListener('click', this.clickHandler);

    // Handle events after the drawer opens
    const transitionDuration = parseFloat(getComputedStyle(this).getPropertyValue('--longest-transition-in-ms'));
    setTimeout(() => {
      if (callback) callback();
      this.dispatchEvent(
        new CustomEvent(`on:${this.dataset.name}:after-open`, {
          bubbles: true,
        }),
      );
    }, transitionDuration);
  }

  /**
   * Closes the drawer.
   * @param {Function} [callback] - Call back function to trigger after the close has completed
   */
  close(callback) {
    this.dispatchEvent(
      new CustomEvent(`on:${this.dataset.name}:before-close`, {
        bubbles: true,
      }),
    );

    this.removeAttribute('open');
    this.setAttribute('aria-hidden', 'true');
    this.overlay.classList.remove('is-visible');

    removeTrapFocus(this.opener);

    // Restore page position and scroll behaviour.
    document.documentElement.style.height = '';
    document.body.style.top = '';
    document.body.classList.remove('fixed');
    window.scrollTo(0, this.scrollY);

    // Remove event listeners added on drawer opening.
    this.removeEventListener('click', this.clickHandler);
    this.removeEventListener('keyup', this.keyupHandler);
    this.overlay.removeEventListener('click', this.clickHandler);

    // Handle events after the drawer closes
    const transitionDuration = parseFloat(getComputedStyle(this).getPropertyValue('--longest-transition-in-ms'));
    setTimeout(() => {
      if (callback) callback();
      this.dispatchEvent(
        new CustomEvent(`on:${this.dataset.name}:after-close`, {
          bubbles: true,
        }),
      );
    }, transitionDuration);
  }
}

customElements.define('side-drawer', SideDrawer);

window.addEventListener(
  'resize',
  debounce(() => {
    window.dispatchEvent(new CustomEvent('on:debounced-resize'));
  }),
);

/**
 * Keeps a record of the height of the first occurrence of the given child element and stores it in
 * a css variable called `--[selector without the . or #]-height`. This height is kept up to date
 * when the browser changes size or the child element mutates.
 *
 * The selector must only match one element, and don't nest watched elements.
 *
 * Example usage:
 * <slide-show data-css-var-height=".quick-nav">
 * ... will result in:
 * <slide-show data-css-var-height=".quick-nav" style="--quick-nav-height: 483px;">
 */
function initCssVarHeightWatch() {
  const parentElems = document.querySelectorAll('[data-css-var-height]');
  if (parentElems) {
    const updateHeight = (elem) => {
      const parentElem = elem.closest('[data-css-var-height]');
      if (parentElem) {
        const selectors = parentElem.dataset.cssVarHeight.split(',');
        let matchedSelector = null;
        selectors.forEach((selector) => {
          if (elem.matches(selector.trim())) {
            matchedSelector = selector.trim();
          }
        });

        const variableName = `--${matchedSelector.replace(/^([#.])/, '')}-height`;
        parentElem.style.setProperty(variableName, `${elem.getBoundingClientRect().height.toFixed(2)}px`);
      }
    };

    let mutationObserver = null;
    if ('MutationObserver' in window) {
      mutationObserver = new MutationObserver(
        debounce((mutationList) => {
          const elemToWatch = mutationList[0].target.closest('[data-css-var-height]');
          if (elemToWatch) {
            updateHeight(elemToWatch.querySelector(elemToWatch.dataset.cssVarHeight));
          }
        }),
      );
    }

    parentElems.forEach((parentElem) => {
      parentElem.dataset.cssVarHeight.split(',').forEach((selector) => {
        const elemToWatch = parentElem.querySelector(selector);
        if (elemToWatch) {
          if (mutationObserver) {
            mutationObserver.observe(elemToWatch, {
              childList: true,
              attributes: true,
              subtree: true,
            });
          }

          window.addEventListener('on:debounced-resize', () => {
            const elem = parentElem.querySelector(selector);
            if (elem) updateHeight(elem);
          });

          document.addEventListener('on:css-var-height:update', () => {
            const elem = parentElem.querySelector(selector);
            if (elem) updateHeight(elem);
          });

          updateHeight(elemToWatch);
        }
      });
    });
  }
}

document.addEventListener('DOMContentLoaded', initCssVarHeightWatch);
document.addEventListener('shopify:section:load', initCssVarHeightWatch);

/**
 * Provides convenient utility functions for interacting with elements
 */
(() => {
  theme.elementUtil = {};

  /**
   * Allows for removal of elements in one line of code
   * @param {object} elem - Element to remove
   */
  theme.elementUtil.remove = (elem) => {
    if (elem) {
      if (typeof elem.remove === 'function') {
        elem.remove();
      } else {
        elem.forEach((thisElem) => {
          thisElem.remove();
        });
      }
    }
  };

  /**
   * Checks if the passed element is in viewport or not
   * @param {object} elem - Element to check the view of
   * @returns {boolean}
   */
  theme.elementUtil.isInViewport = (elem) => {
    const rect = elem.getBoundingClientRect();
    return (
      Math.round(rect.top) >= 0 &&
      Math.round(rect.left) >= 0 &&
      Math.round(rect.bottom) <= (window.innerHeight || document.documentElement.clientHeight) &&
      Math.round(rect.right) <= (window.innerWidth || document.documentElement.clientWidth)
    );
  };
})();

/**
 * Utility functions for interacting with LocalStorage/SessionStorage
 */
(() => {
  theme.storageUtil = {};

  theme.storageUtil.set = (key, value, isSession) => {
    if (isSession) {
      sessionStorage.setItem(`cc-${key}`, typeof value === 'object' ? JSON.stringify(value) : value);
    } else {
      localStorage.setItem(`cc-${key}`, typeof value === 'object' ? JSON.stringify(value) : value);
    }
  };

  theme.storageUtil.get = (key, isJson, isSession) => {
    let value = isSession ? sessionStorage.getItem(`cc-${key}`) : localStorage.getItem(`cc-${key}`);
    if (isJson) {
      value = JSON.parse(value);
    }
    return value;
  };

  theme.storageUtil.remove = (key, isSession) => {
    if (isSession) {
      sessionStorage.removeItem(`cc-${key}`);
    } else {
      localStorage.removeItem(`cc-${key}`);
    }
  };
})();

class StoreHeader extends HTMLElement {
  constructor() {
    super();
    this.menu = this.querySelector('.main-menu__content');
    this.searchToggle = this.querySelector('.js-show-search');
    this.searchToggleLeft = this.querySelector('.js-show-search-left');
    this.mobNavToggle = this.querySelector('.main-menu__toggle');
    this.shakeyCartIcon = this.querySelector('.header__icon--cart-shake');
    this.headerGroupSections = document.querySelectorAll('.shopify-section-group-header-group');

    this.stickyInitialised = false;
    this.stickyTransitioning = false;
    this.lastScrollPos = 0;

    this.headerTransitionSpeed = parseFloat(getComputedStyle(this).getPropertyValue('--header-transition-speed'));

    window.setHeaderHeight();
    this.bindEvents();
    this.init();

    document.addEventListener('DOMContentLoaded', this.setMenuHeight.bind(this));
  }

  disconnectedCallback() {
    window.removeEventListener('on:debounced-resize', this.resizeHandler);
    if (this.breakpointChangeHandler) {
      window.removeEventListener('on:breakpoint-change', this.breakpointChangeHandler);
    }
  }

  bindEvents() {
    this.resizeHandler = this.resizeHandler || this.updateHeaderHeights.bind(this);
    window.addEventListener('on:debounced-resize', this.resizeHandler);

    this.mobNavToggle.addEventListener('click', window.setHeaderHeight);
    this.mobNavToggle.addEventListener('click', this.setHeaderEnd.bind(this));

    if (this.dataset.isSticky) {
      this.breakpointChangeHandler = this.breakpointChangeHandler || this.init.bind(this);
      window.addEventListener('on:breakpoint-change', this.breakpointChangeHandler);
    }

    if (this.dataset.isSearchMinimised) {
      if (this.searchToggle) {
        this.searchToggle.addEventListener('click', this.handleSearchToggleClick.bind(this));
      }

      if (this.searchToggleLeft) {
        this.searchToggleLeft.addEventListener('click', this.handleSearchToggleClick.bind(this));
      }
    }
  }

  /**
   * Init sticky behaviour of the header
   */
  init() {
    this.updateHeaderHeights();

    if (this.dataset.isSticky) {
      if (theme.mediaMatches.md && !this.stickyInitialised) {
        this.stickyInitialised = true;
        // Animate the menu in/out on scroll up/down
        window.addEventListener('scroll', this.handleScroll.bind(this));
      }

      setTimeout(() => {
        document.querySelector('.cc-header').classList.add('cc-header--sticky');
      });

      this.setMenuHeight();
      this.setHeaderEnd();
    }

    // Stop the cart icon from shaking when necessary
    if (this.shakeyCartIcon) {
      let pageCount = theme.storageUtil.get('shake-page-count', false, true);
      pageCount = pageCount ? parseInt(pageCount, 10) + 1 : 1;
      const shakeFrequency = parseInt(this.shakeyCartIcon.dataset.shakeFrequency, 10);
      if (pageCount < shakeFrequency) {
        this.shakeyCartIcon.classList.remove('header__icon--cart-shake');
      } else {
        pageCount = 0;
      }
      theme.storageUtil.set('shake-page-count', pageCount, true);
    }
  }

  /**
   * Toggles visibility of the search bar
   * @param {object} evt - Event object
   */
  handleSearchToggleClick(evt) {
    evt.preventDefault();
    const searchBar = this.querySelector('.js-search-bar');
    if (this.classList.contains('search-is-collapsed')) {
      this.classList.remove('search-is-collapsed');

      // Wait for reveal animation to complete
      setTimeout(() => {
        this.classList.add('search-is-visible');
        const searchInput = searchBar.querySelector('.js-search-input');
        searchInput.focus();
        window.setHeaderHeight();
      }, this.headerTransitionSpeed);
    } else {
      this.classList.remove('search-is-visible');

      setTimeout(() => {
        this.classList.add('search-is-collapsed');
      });

      setTimeout(window.setHeaderHeight, this.headerTransitionSpeed);
    }
  }

  /**
   * Wrapper for calling the two below
   */
  updateHeaderHeights() {
    if (theme.mediaMatches.md) {
      this.setMenuHeight();
      setTimeout(window.setHeaderHeight, this.headerTransitionSpeed);
    } else {
      window.setHeaderHeight();
    }

    // Set a css variable to record where the page content starts
    if (this.headerGroupSections && this.headerGroupSections.length > 0) {
      let headerGroupHeight = 0;
      this.headerGroupSections.forEach((section) => {
        headerGroupHeight += section.getBoundingClientRect().height;
      });

      if (headerGroupHeight > 0) {
        document.documentElement.style.setProperty('--content-start', `${headerGroupHeight.toFixed(1)}px`);
      }
    }
  }

  /**
   * Set a css variable to the height of the menu links
   */
  setMenuHeight() {
    if (this.menu && this.menu.clientHeight) {
      this.style.setProperty('--menu-height', `${this.menu.clientHeight + 16}px`);
      document.documentElement.style.setProperty('--header-height', `${this.clientHeight}px`);
    }
  }

  /**
   * Handles 'scroll' event on the header
   */
  handleScroll() {
    if (!document.body.classList.contains('fixed') && !this.stickyTransitioning) {
      if (document.documentElement.scrollTop < 200) {
        this.show();
      } else if (this.lastScrollPos < document.documentElement.scrollTop) {
        this.hide();
      } else if (this.lastScrollPos - 5 > document.documentElement.scrollTop) {
        this.show();
      }

      this.lastScrollPos = document.documentElement.scrollTop;
    }
  }

  /**
   * Set a css variable to indicate where the nav ends on the page
   */
  setHeaderEnd() {
    const headerEnd = Number(this.getBoundingClientRect().top + this.clientHeight);
    document.documentElement.style.setProperty('--header-end', `${headerEnd.toFixed(1)}px`);
    document.documentElement.style.setProperty(
      '--header-end-padded',
      `${(headerEnd + (theme.mediaMatches.md ? 56 : 20)).toFixed(1)}px`,
    );
  }

  /**
   * Updates hidden class for header element
   */
  show() {
    this.classList.remove('is-out');
    this.stickyTransitioning = true;
    setTimeout(() => {
      this.lastScrollPos = document.documentElement.scrollTop;
      this.stickyTransitioning = false;
      this.handleScroll();
      this.setHeaderEnd();
    }, 300);
  }

  /**
   * Updates hidden class for header element
   */
  hide() {
    if (!this.stickyTransitioning) {
      this.classList.add('is-out');
      this.stickyTransitioning = true;
      setTimeout(() => {
        this.lastScrollPos = document.documentElement.scrollTop;
        this.stickyTransitioning = false;
        this.handleScroll();
        this.setHeaderEnd();
      }, 300);
    }
  }
}

customElements.define('store-header', StoreHeader);

class MainMenu extends HTMLElement {
  constructor() {
    super();
    this.mainDisclosure = this.querySelector('.main-menu__disclosure');
    this.mainToggle = this.querySelector('.main-menu__toggle');
    this.firstLevelMenuLinks = this.querySelectorAll('.js-nav-hover');
    this.firstLevelSingleLinks = this.querySelectorAll('.main-nav__item--primary:not(.main-nav__item-content)');
    this.nav = this.querySelector('.main-nav');
    this.overlay = document.querySelector('.js-overlay');
    this.searchIcon = document.querySelector('.header__icons .js-show-search');
    this.sidebarLinks = this.querySelectorAll('.js-sidebar-hover');
    this.elementsWhichCloseMenus = document.querySelectorAll('.js-closes-menu');

    this.childNavOpen = false;
    this.overlayOpen = false;

    this.addListeners();
    this.init();

    // Reorder nav containers so DOM/tab order matches visual order at all breakpoints.
    // WEB-18768: three layouts. Mobile (<769px): primary (shopping) on top.
    // Stacked desktop: secondary nav on top. Side-by-side (>=1500px, when it
    // fits): primary nav on the left.
    // WEB-18041: whether the desktop nav stacks is now decided by measuring
    // whether the two nav groups actually fit on one row — not by viewport width
    // alone. Browser zoom changes the viewport (media queries react), but
    // assistive text-scaling (Userway "Bigger Text" / "Text Spacing") widens the
    // text without changing the viewport, so only measuring actual fit catches it.
    this.contentContainer = this.querySelector('.main-menu__content');
    this.primaryNav = this.querySelector('.primary-nav-container');
    this.secondaryNav = this.querySelector('.secondary-nav-container');

    if (this.contentContainer && this.primaryNav && this.secondaryNav) {
      // Mobile (<769px) uses the drawer layout (shopping menu on top).
      this.navMobileMq = window.matchMedia('(max-width: 768.98px)');
      this.boundHandleNavOrder = this.handleNavOrder.bind(this);
      try {
        this.navMobileMq.addEventListener('change', this.boundHandleNavOrder);
      } catch (err) {
        // Fallback for legacy browsers (Safari < 14).
        this.navMobileMq.addListener(this.boundHandleNavOrder);
      }

      // Re-measure when the nav's own size changes (e.g. text scaling) even
      // though the viewport hasn't.
      if ('ResizeObserver' in window) {
        this.navResizeObserver = new ResizeObserver(() => this.scheduleNavLayout());
        this.navResizeObserver.observe(this.contentContainer);
        this.navResizeObserver.observe(this.primaryNav);
        this.navResizeObserver.observe(this.secondaryNav);
      }

      this.handleNavOrder();
    }
  }

  disconnectedCallback() {
    window.removeEventListener('focusin', this.focusOutHandler);
    window.removeEventListener('on:breakpoint-change', this.breakpointChangeHandler);
    this.nav.removeEventListener('keydown', this.keydownHandler);
    this.nav.removeEventListener('focusout', this.navFocusOutHandler);

    if (this.navMobileMq && this.boundHandleNavOrder) {
      try {
        this.navMobileMq.removeEventListener('change', this.boundHandleNavOrder);
      } catch (err) {
        this.navMobileMq.removeListener(this.boundHandleNavOrder);
      }
    }

    if (this.navResizeObserver) this.navResizeObserver.disconnect();

    // Cancel a pending nav-layout frame so it can't run against a detached
    // element if the section re-renders (e.g. in the Theme Editor).
    if (this._navLayoutRaf) {
      cancelAnimationFrame(this._navLayoutRaf);
      this._navLayoutRaf = null;
    }

    if (Shopify.designMode) {
      document.removeEventListener('shopify:block:select', this.blockSelectHandler);
      document.removeEventListener('shopify:block:deselect', this.blockDeselectHandler);
    }
  }

  addListeners() {
    this.focusOutHandler = this.focusOutHandler || this.handleFocusOut.bind(this);
    this.breakpointChangeHandler = this.breakpointChangeHandler || this.init.bind(this);

    this.keydownHandler = this.keydownHandler || this.handleKeydown.bind(this);
    this.navFocusOutHandler = this.navFocusOutHandler || this.handleNavFocusOut.bind(this);

    this.mainDisclosure.addEventListener('transitionend', MainMenu.handleTransition.bind(this));
    this.mainToggle.addEventListener('click', this.handleMainMenuToggle.bind(this));
    this.nav.addEventListener('click', MainMenu.handleNavClick.bind(this));
    this.nav.addEventListener('keydown', this.keydownHandler);
    this.nav.addEventListener('focusout', this.navFocusOutHandler);
    window.addEventListener('focusin', this.focusOutHandler);
    window.addEventListener('on:breakpoint-change', this.breakpointChangeHandler);

    if (Shopify.designMode) {
      this.blockSelectHandler = this.blockSelectHandler || this.handleBlockSelect.bind(this);
      this.blockDeselectHandler = this.blockDeselectHandler || this.handleBlockDeselect.bind(this);
      document.addEventListener('shopify:block:select', this.blockSelectHandler);
      document.addEventListener('shopify:block:deselect', this.blockDeselectHandler);
    }

    if (!theme.mediaMatches.md && this.searchIcon) {
      this.searchIcon.addEventListener('click', this.closeMainDisclosure.bind(this));
    }
  }

  /**
   * Sets 'open' state of the main disclosure element.
   * @param {?object} evt - Event object.
   */
  init(evt) {
    if (!evt) {
      this.mainDisclosure.open = theme.mediaMatches.md;
    } else if (!theme.mediaMatches.md && !this.childNavOpen) {
      this.close(this.mainDisclosure);

      if (this.overlayOpen) this.toggleOverlay(false);
    } else {
      // If there's another menu open, close it.
      const activeDisclosure = this.nav.querySelector(':scope > li > details.is-open');
      if (activeDisclosure) {
        this.close(activeDisclosure);
      } else {
        MainMenu.open(this.mainDisclosure, false);
      }

      if (!this.childNavOpen) {
        if (this.overlayOpen) this.toggleOverlay(false);
      }
    }

    // Close the submenus (they're open for no-js)
    this.querySelectorAll('.child-nav--dropdown details[open]').forEach((childToggle) => {
      childToggle.removeAttribute('open');
    });

    if (theme.device.hasHover) {
      // Add event handler (so the bound event listener can be removed)
      this.mouseEnterMenuLinkHandler = this.mouseEnterMenuLinkHandler || this.handleMouseEnterMenuLink.bind(this);
      this.mouseLeaveMenuLinkHandler = this.mouseLeaveMenuLinkHandler || this.handleMouseLeaveMenuLink.bind(this);
      this.mouseEnterSingleLinkHandler = this.mouseEnterSingleLinkHandler || this.handleMouseEnterSingleLink.bind(this);
      this.mouseLeaveSingleLinkHandler = this.mouseLeaveSingleLinkHandler || this.handleMouseLeaveSingleLink.bind(this);
      this.mouseEnterMenuCloserHandler = this.mouseEnterMenuCloserHandler || this.handleClose.bind(this);

      // Bind listening events for mouse enter/leave a main menu link
      if (!this.mouseOverListening && theme.mediaMatches.md) {
        this.firstLevelMenuLinks.forEach((menuLink) => {
          menuLink.addEventListener('mouseenter', this.mouseEnterMenuLinkHandler);
          menuLink.addEventListener('mouseleave', this.mouseLeaveMenuLinkHandler);
        });
        this.firstLevelSingleLinks.forEach((singleLink) => {
          singleLink.addEventListener('mouseenter', this.mouseEnterSingleLinkHandler);
          singleLink.addEventListener('mouseleave', this.mouseLeaveSingleLinkHandler);
        });
        this.elementsWhichCloseMenus.forEach((elem) => {
          elem.addEventListener('mouseenter', this.mouseEnterMenuCloserHandler);
        });
        this.mouseOverListening = true;
      } else if (this.mouseOverListening && !theme.mediaMatches.md) {
        this.firstLevelMenuLinks.forEach((menuLink) => {
          menuLink.removeEventListener('mouseenter', this.mouseEnterMenuLinkHandler);
          menuLink.removeEventListener('mouseleave', this.mouseLeaveMenuLinkHandler);
        });
        this.firstLevelSingleLinks.forEach((singleLink) => {
          singleLink.removeEventListener('mouseenter', this.mouseEnterSingleLinkHandler);
          singleLink.removeEventListener('mouseleave', this.mouseLeaveSingleLinkHandler);
        });
        this.elementsWhichCloseMenus.forEach((elem) => {
          elem.removeEventListener('mouseenter', this.mouseEnterMenuCloserHandler);
        });
        this.mouseOverListening = false;
      }

      if (this.sidebarLinks) {
        if (!this.mouseOverSidebarListening && theme.mediaMatches.md) {
          this.sidebarLinks.forEach((sidebarLink) => {
            sidebarLink.addEventListener('mouseenter', MainMenu.handleSidenavMenuToggle);
          });
          this.mouseOverSidebarListening = true;
        } else if (this.mouseOverSidebarListening && !theme.mediaMatches.md) {
          this.sidebarLinks.forEach((sidebarLink) => {
            sidebarLink.removeEventListener('mouseenter', MainMenu.handleSidenavMenuToggle);
          });
          this.mouseOverSidebarListening = false;
        }
      }
    }

    // Keyboard focusin on sidebar items — registered outside the hasHover block
    // so non-hover devices (e.g., tablets with external keyboards) can still
    // activate panels when arrow-key-navigating between sidebar items.
    if (this.sidebarLinks) {
      this.focusinSidebarHandler =
        this.focusinSidebarHandler ||
        function (evt) {
          MainMenu.handleSidenavMenuToggle(evt, evt.currentTarget);
        };

      if (!this.focusinSidebarListening && theme.mediaMatches.md) {
        this.sidebarLinks.forEach((sidebarLink) => {
          sidebarLink.addEventListener('focusin', this.focusinSidebarHandler);
        });
        this.focusinSidebarListening = true;
      } else if (this.focusinSidebarListening && !theme.mediaMatches.md) {
        this.sidebarLinks.forEach((sidebarLink) => {
          sidebarLink.removeEventListener('focusin', this.focusinSidebarHandler);
        });
        this.focusinSidebarListening = false;
      }
    }
  }

  /**
   * Coalesces ResizeObserver callbacks to one layout pass per frame.
   */
  scheduleNavLayout() {
    if (this._navLayoutRaf) return;
    this._navLayoutRaf = requestAnimationFrame(() => {
      this._navLayoutRaf = null;
      this.handleNavOrder();
    });
  }

  /**
   * Decides whether the desktop nav should stack and keeps the DOM/tab order in
   * sync with the visual order.
   *
   * WEB-18041: "stacked" is now measured, not assumed from viewport width. The
   * two nav groups stack whenever they can't sit on one row — whether that's a
   * narrow viewport, browser zoom, or assistive text-scaling (Userway) that
   * widens the text without changing the viewport. When stacked, the secondary
   * (quicklinks) nav goes on top; mobile and side-by-side keep the primary nav
   * first.
   */
  handleNavOrder() {
    const STACKED_CLASS = 'main-menu__content--stacked';

    // Mobile (<769px): drawer layout — shopping menu on top, never force-stacked.
    if (this.navMobileMq.matches) {
      this.contentContainer.classList.remove(STACKED_CLASS);
      this.setSecondaryFirst(false);
      return;
    }

    // Desktop: measure fit in the un-stacked state. Doing this synchronously
    // means a stable result ends at the same class/size it started with, so the
    // ResizeObserver settles instead of looping.
    this.contentContainer.classList.remove(STACKED_CLASS);
    const differentRows = this.primaryNav.offsetTop !== this.secondaryNav.offsetTop; // forces a reflow read
    const overflows = this.contentContainer.scrollWidth > this.contentContainer.clientWidth + 1;
    const stacked = differentRows || overflows;

    this.contentContainer.classList.toggle(STACKED_CLASS, stacked);
    this.setSecondaryFirst(stacked);
  }

  /**
   * Positions the secondary nav in the DOM. When secondaryFirst is true it sits
   * before the primary nav (stacked desktop: secondary on top).
   *
   * WEB-18768: We only ever move the lightweight secondary nav, never the primary
   * nav. The primary nav holds the <nav-menu> mega-nav custom elements; re-inserting
   * it would disconnect/reconnect them all, re-initializing the mega-nav and blanking
   * it for a noticeable beat on slower devices. Moving only the secondary nav keeps
   * DOM/tab order correct without ever tearing down the mega-nav.
   */
  setSecondaryFirst(secondaryFirst) {
    if (secondaryFirst) {
      if (this.secondaryNav.nextElementSibling !== this.primaryNav) {
        this.contentContainer.insertBefore(this.secondaryNav, this.primaryNav);
      }
    } else if (this.primaryNav.nextElementSibling !== this.secondaryNav) {
      this.contentContainer.insertBefore(this.secondaryNav, this.primaryNav.nextElementSibling);
    }
  }

  /**
   * Close the menu if the nav loses focus
   * @param {object} evt - Event object.
   */
  handleFocusOut(evt) {
    if (!this.contains(evt.target) && this.overlayOpen) this.handleClose();
  }

  /**
   * Closes the active dropdown and moves focus to the next top-level nav item.
   * If on the last nav item, focuses the trigger summary so the next Tab
   * exits the nav naturally.
   * @param {Element} disclosure - The active disclosure element to close.
   */
  _closeAndFocusNextTopLevelItem(disclosure) {
    this.close(disclosure, true);
    this.toggleOverlay(false);
    this.childNavOpen = false;
    const currentTab = disclosure.closest('.nav-tab');
    const topLevelItems = Array.from(this.nav.querySelectorAll(':scope > li.nav-tab'));
    const tabIndex = currentTab ? topLevelItems.indexOf(currentTab) : -1;
    if (tabIndex >= 0 && tabIndex < topLevelItems.length - 1) {
      const nextTab = topLevelItems[tabIndex + 1];
      const nextSummary = nextTab.querySelector('summary');
      const nextLink = nextTab.querySelector(':scope > a');
      if (nextSummary) nextSummary.focus();
      else if (nextLink) nextLink.focus();
    } else {
      // Last top-level item: focus the trigger so next Tab exits the nav
      const trigger = disclosure.querySelector('summary');
      if (trigger) trigger.focus();
    }
  }

  /**
   * Handles keydown events for keyboard navigation within the mega-nav.
   * Escape closes the dropdown, arrows navigate between items.
   * @param {KeyboardEvent} evt
   */
  handleKeydown(evt) {
    if (!theme.mediaMatches.md) return;

    const activeDisclosure = this.nav.querySelector(':scope > li > details.is-open');

    // Escape: if inside a panel, return to sidebar item; otherwise close dropdown
    if (evt.key === 'Escape' && activeDisclosure) {
      const inPanel = evt.target.closest('.disclosure__panel');
      if (inPanel) {
        // Return to the sidebar item that owns this panel.
        // Set flag so the keyup-based handleClose() skips this Escape.
        evt.preventDefault();
        this._escapedFromPanel = true;
        const parentSidebar = inPanel.previousElementSibling;
        const sidebarItems = Array.from(activeDisclosure.querySelectorAll('.js-sidebar-hover'));
        if (parentSidebar && sidebarItems.includes(parentSidebar)) {
          parentSidebar.focus();
        }
        return;
      }
      evt.preventDefault();
      const trigger = activeDisclosure.querySelector('summary');
      this.close(activeDisclosure, true);
      this.toggleOverlay(false);
      this.childNavOpen = false;
      if (trigger) trigger.focus();
      return;
    }

    // ArrowLeft / ArrowRight on top-level: move between nav items
    // (only when focus is on a top-level summary/link, not inside dropdown content)
    if (evt.key === 'ArrowLeft' || evt.key === 'ArrowRight') {
      const isInsideDropdown = evt.target.closest('.main-nav__child');
      if (!isInsideDropdown) {
        const topLevelItems = Array.from(this.nav.querySelectorAll(':scope > li.nav-tab'));
        const focusedTab = evt.target.closest('.nav-tab');
        if (!focusedTab || !topLevelItems.includes(focusedTab)) return;

        evt.preventDefault();
        const currentIndex = topLevelItems.indexOf(focusedTab);
        const direction = evt.key === 'ArrowRight' ? 1 : -1;
        const nextIndex = (currentIndex + direction + topLevelItems.length) % topLevelItems.length;
        const nextTab = topLevelItems[nextIndex];

        // Close current dropdown if open
        if (activeDisclosure) {
          this.close(activeDisclosure);
          this.toggleOverlay(false);
          this.childNavOpen = false;
        }

        // Focus the next item's summary or link
        const nextSummary = nextTab.querySelector('summary');
        const nextLink = nextTab.querySelector(':scope > a');
        if (nextSummary) {
          nextSummary.focus();
        } else if (nextLink) {
          nextLink.focus();
        }
        return;
      }
      // When inside dropdown, fall through to panel-specific ArrowRight/ArrowLeft handlers below
    }

    // Two-level Tab navigation inside dropdown:
    //   Level 1: Tab cycles through sidebar items only
    //   Level 2: Enter/ArrowRight dives into the active panel's links;
    //            Escape/ArrowLeft returns to the sidebar item
    // Without this, natural DOM order sends Tab through grandchild links
    // before reaching the next sidebar item (they're children of the same <details>).
    if (evt.key === 'Tab' && activeDisclosure) {
      const dropdownContent = activeDisclosure.querySelector('.main-nav__child');
      if (dropdownContent) {
        const sidebarItems = Array.from(dropdownContent.querySelectorAll('.js-sidebar-hover'));
        const inPanel = evt.target.closest('.disclosure__panel');
        const onSidebar = evt.target.closest('.js-sidebar-hover') || sidebarItems.includes(evt.target);

        if (inPanel) {
          // --- Inside a panel: Tab cycles through panel links ---
          // Filter out hidden elements (e.g., mobile-only links with md:hidden)
          const panelLinks = Array.from(
            inPanel.querySelectorAll('a[href]:not([tabindex="-1"]), button:not([disabled])'),
          ).filter((el) => el.offsetParent !== null);
          const currentIndex = panelLinks.indexOf(evt.target);

          if (evt.shiftKey) {
            if (currentIndex > 0) {
              evt.preventDefault();
              panelLinks[currentIndex - 1].focus();
            } else {
              // Shift+Tab from first panel link → back to the sidebar item
              evt.preventDefault();
              const parentSidebar = inPanel.previousElementSibling;
              if (parentSidebar && sidebarItems.includes(parentSidebar)) {
                parentSidebar.focus();
              }
            }
          } else {
            if (currentIndex >= 0 && currentIndex < panelLinks.length - 1) {
              evt.preventDefault();
              panelLinks[currentIndex + 1].focus();
            } else {
              // Tab past last panel link → next sidebar item, or exit dropdown
              evt.preventDefault();
              const parentSidebar = inPanel.previousElementSibling;
              const sidebarIndex = parentSidebar ? sidebarItems.indexOf(parentSidebar) : -1;
              if (sidebarIndex >= 0 && sidebarIndex < sidebarItems.length - 1) {
                sidebarItems[sidebarIndex + 1].focus();
              } else {
                // Last sidebar's panel → close and focus next top-level nav item
                this._closeAndFocusNextTopLevelItem(activeDisclosure);
              }
            }
          }
          return;
        }

        if (onSidebar && sidebarItems.length > 0) {
          // --- On a sidebar item: Tab moves between sidebar items ---
          const closestSidebar = evt.target.closest('.js-sidebar-hover') || evt.target;
          const currentIndex = sidebarItems.indexOf(closestSidebar);

          if (evt.shiftKey) {
            if (currentIndex > 0) {
              evt.preventDefault();
              sidebarItems[currentIndex - 1].focus();
            }
            // currentIndex <= 0: let Shift+Tab exit to the trigger summary
          } else {
            if (currentIndex >= 0 && currentIndex < sidebarItems.length - 1) {
              evt.preventDefault();
              sidebarItems[currentIndex + 1].focus();
            } else {
              // Tab past last sidebar item → close and focus next top-level nav item
              evt.preventDefault();
              this._closeAndFocusNextTopLevelItem(activeDisclosure);
            }
          }
          return;
        }

        // Not on sidebar or in panel (e.g., on trigger summary): enter dropdown
        if (!evt.shiftKey && sidebarItems.length > 0) {
          evt.preventDefault();
          sidebarItems[0].focus();
          return;
        }
      }
    }

    // Enter / ArrowRight on a sidebar item: dive into that panel's links
    if ((evt.key === 'Enter' || evt.key === 'ArrowRight') && activeDisclosure) {
      const sidebarItem = evt.target.closest('.js-sidebar-hover') || evt.target;
      const sidebarItems = Array.from(activeDisclosure.querySelectorAll('.js-sidebar-hover'));
      if (sidebarItems.includes(sidebarItem)) {
        const panel = sidebarItem.nextElementSibling;
        if (panel && panel.classList.contains('disclosure__panel')) {
          // Find first visible link (skip hidden mobile-only elements)
          const firstLink = Array.from(
            panel.querySelectorAll('a[href]:not([tabindex="-1"]), button:not([disabled])'),
          ).find((el) => el.offsetParent !== null);
          if (firstLink) {
            evt.preventDefault();
            firstLink.focus();
            return;
          }
        }
      }
    }

    // ArrowLeft from inside a panel: return to the sidebar item
    if (evt.key === 'ArrowLeft' && activeDisclosure) {
      const inPanel = evt.target.closest('.disclosure__panel');
      if (inPanel) {
        evt.preventDefault();
        const parentSidebar = inPanel.previousElementSibling;
        const sidebarItems = Array.from(activeDisclosure.querySelectorAll('.js-sidebar-hover'));
        if (parentSidebar && sidebarItems.includes(parentSidebar)) {
          parentSidebar.focus();
        }
        return;
      }
    }

    // ArrowDown / ArrowUp inside dropdown: move between sidebar items
    if ((evt.key === 'ArrowDown' || evt.key === 'ArrowUp') && activeDisclosure) {
      const sidebarItems = Array.from(activeDisclosure.querySelectorAll('.js-sidebar-hover'));
      if (sidebarItems.length === 0) return;

      const focusedSidebar =
        evt.target.closest('.js-sidebar-hover') || (sidebarItems.includes(evt.target) ? evt.target : null);

      evt.preventDefault();

      if (!focusedSidebar || !sidebarItems.includes(focusedSidebar)) {
        // Focus is elsewhere in the dropdown — move to first sidebar item
        sidebarItems[0].focus();
      } else {
        const currentIndex = sidebarItems.indexOf(focusedSidebar);
        const direction = evt.key === 'ArrowDown' ? 1 : -1;
        const nextIndex = (currentIndex + direction + sidebarItems.length) % sidebarItems.length;
        sidebarItems[nextIndex].focus();
      }
      // The focusin handler will activate the panel
      return;
    }
  }

  /**
   * Closes the active dropdown when focus moves outside it.
   * Uses requestAnimationFrame because focusout fires before the new element receives focus.
   * @param {FocusEvent} evt
   */
  handleNavFocusOut(evt) {
    if (!theme.mediaMatches.md) return;
    const activeDisclosure = this.nav.querySelector(':scope > li > details.is-open');
    if (!activeDisclosure) return;

    requestAnimationFrame(() => {
      const focused = document.activeElement;
      if (!activeDisclosure.contains(focused) && !activeDisclosure.querySelector('summary')?.contains(focused)) {
        this.close(activeDisclosure, true);
        this.toggleOverlay(false);
        this.childNavOpen = false;
      }
    });
  }

  /**
   * Updates the visible sidebar item
   * @param {object} evt - Event object.
   * @param {Element} [summaryElem] - The summary element to open.
   */
  static handleSidenavMenuToggle(evt, summaryElem = evt.target) {
    const container = summaryElem.closest('.child-nav');
    const lastSidenavElem = container.querySelector('.is-visible');
    if (lastSidenavElem) {
      lastSidenavElem.classList.remove('is-visible');
    }
    summaryElem.classList.add('is-visible');

    // Maintain a CSS variable which records the height of the current sidebar links
    const menu = summaryElem.closest('nav-menu');
    if (menu) {
      const openDisclosure = menu.querySelector('.disclosure__panel');
      if (openDisclosure) {
        container.style.setProperty(
          '--sidebar-height',
          `${Number.parseInt(openDisclosure.getBoundingClientRect().height, 10)}px`,
        );
      }
    }
  }

  /**
   * Handles 'toggle' event on the main disclosure element.
   * @param {object} evt - Event object.
   */
  handleMainMenuToggle(evt) {
    evt.preventDefault();
    this.opener = this.mainToggle;

    if (!this.mainDisclosure.open) {
      MainMenu.open(this.mainDisclosure);
    } else {
      this.close(this.mainDisclosure, true);
    }
  }

  /**
   * Handles 'mouseenter' event on the main menu items using a timeout to infer hover intent
   * @param {object} evt - Event object
   */
  handleMouseEnterMenuLink(evt) {
    this.menuLinkTimeout = setTimeout(
      this.openMenuFromMouseEnter.bind(this, evt.target),
      Number.parseInt(this.dataset.menuSensitivity, 10),
    );
  }

  /**
   * Handles 'mouseleave' event on the main menu items - clears the timeout
   */
  handleMouseLeaveMenuLink() {
    if (this.menuLinkTimeout) clearTimeout(this.menuLinkTimeout);
  }

  /**
   * Handles 'mouseenter' event on links with no submenu items using a timeout to infer hover intent
   */
  handleMouseEnterSingleLink() {
    this.singleLinkTimeout = setTimeout(
      () => {
        this.handleClose();
      },
      Number.parseInt(this.dataset.menuSensitivity, 10),
    );
  }

  /**
   * Handles 'mouseleave' event on links with no submenu - clears the timeout
   */
  handleMouseLeaveSingleLink() {
    if (this.singleLinkTimeout) clearTimeout(this.singleLinkTimeout);
  }

  /**
   * Opens the menu being hovered over
   * @param {Element} menuElem - The menu element to open.
   */
  openMenuFromMouseEnter(menuElem) {
    const disclosure = menuElem.closest('details');
    if (!disclosure.classList.contains('is-open')) {
      const activeDisclosure = this.nav.querySelector(':scope > li > details.is-open');

      // If there's another menu open, close it.
      if (activeDisclosure && activeDisclosure !== disclosure) {
        this.close(activeDisclosure);
      } else {
        this.toggleOverlay(!this.overlayOpen);
      }

      MainMenu.open(disclosure);
    }
  }

  /**
   * Handles 'click' event on the nav.
   * @param {object} evt - Event object.
   */
  static handleNavClick(evt) {
    const mainMenuContent = evt.target.closest('.main-menu__content');
    let el = evt.target;

    // Handle sidebar link clicks
    if (theme.mediaMatches.md && el.matches('.js-sidebar-hover') && el.closest('summary')) {
      evt.preventDefault();
      MainMenu.handleSidenavMenuToggle(evt);
    }

    // Don't follow # links
    if (evt.target.href && evt.target.href.endsWith('#')) evt.preventDefault();

    // If we're on a device without hover on a larger screen, open the menu
    if (theme.mediaMatches.md && !theme.device.hasHover) {
      el = evt.target.closest('.js-toggle');
      if (!el) return;
    }

    if (!el.matches('.js-toggle,.js-back')) return;

    const disclosure = el.closest('details');

    if (theme.mediaMatches.md && theme.device.hasHover) {
      evt.preventDefault(); // Prevent native <details> toggle (we manage it)

      if (!disclosure.classList.contains('is-open')) {
        // Close any previously open dropdown
        const activeDisclosure = this.nav.querySelector(':scope > li > details.is-open');
        if (activeDisclosure && activeDisclosure !== disclosure) {
          this.close(activeDisclosure);
        }

        MainMenu.open(disclosure);
        this.toggleOverlay(true);
        this.childNavOpen = true;
        // Focus stays on the trigger summary; user presses Tab to enter the dropdown.
        // This matches standard disclosure navigation (Enter opens, Tab enters).
      } else {
        this.close(disclosure, true);
        this.toggleOverlay(false);
        this.childNavOpen = false;
      }
      return;
    }

    this.opener = el;

    if (el.matches('.js-toggle')) {
      evt.preventDefault();

      if (!theme.mediaMatches.md) {
        mainMenuContent.classList.add('main-menu__content--no-focus');
      }

      if (!disclosure.classList.contains('is-open')) {
        this.childNavOpen = true;

        const activeDisclosure = this.nav.querySelector(':scope > li > details.is-open');
        // If there's another menu open, close it.
        if (activeDisclosure && activeDisclosure !== disclosure) {
          this.close(activeDisclosure);
        } else if (theme.mediaMatches.md) {
          this.toggleOverlay(!this.overlayOpen);
        }

        MainMenu.open(disclosure);
      } else {
        this.close(disclosure, true);
        this.childNavOpen = false;
        this.toggleOverlay(false);
      }
    } else if (el.matches('.js-back')) {
      evt.preventDefault();
      this.close(disclosure, true);
      this.childNavOpen = false;

      if (!theme.mediaMatches.md) {
        mainMenuContent.classList.remove('main-menu__content--no-focus');
      }
    }
  }

  /**
   * Handles 'transitionend' event on the nav.
   * @param {object} evt - Event object.
   */
  static handleTransition(evt) {
    // Only act on transitions from dropdown panels (.main-nav__child) or the
    // main menu content (.main-menu__content), not from nested elements like
    // sidebar panels whose transitionend could accidentally close inner <details>.
    if (!evt.target.matches('.main-nav__child, .main-menu__content')) return;

    const disclosure = evt.target.closest('details');

    if (disclosure && disclosure.classList.contains('is-closing')) {
      clearTimeout(disclosure._closeTimeout);
      disclosure.classList.remove('is-closing');
      disclosure.open = false;

      removeTrapFocus();
      this.opener = null;
    }
  }

  /**
   * Handles a 'click' event on the overlay and a 'keyup' event on the nav.
   * @param {object} evt - Event object.
   */
  handleClose(evt) {
    if (evt && evt.type === 'keyup' && evt.key !== 'Escape') return;

    // Skip if the Escape was already handled by the in-panel keydown handler
    if (evt && evt.type === 'keyup' && evt.key === 'Escape' && this._escapedFromPanel) {
      this._escapedFromPanel = false;
      return;
    }

    const disclosure = theme.mediaMatches.md
      ? this.nav.querySelector(':scope > li > details.is-open')
      : this.mainDisclosure;

    if (disclosure) {
      this.close(disclosure, true);
      this.toggleOverlay(false);
      this.childNavOpen = false;
    }
  }

  /**
   * Toggles visibility of the background overlay.
   * @param {boolean} show - Show the overlay.
   */
  toggleOverlay(show) {
    this.overlayOpen = show;
    this.overlay.classList.toggle('overlay--nav', show);
    this.overlay.classList.toggle('is-visible', show);

    if (show) {
      // Add event handler (so the bound event listener can be removed).
      this.closeHandler = this.closeHandler || this.handleClose.bind(this);

      // Add event listeners (for while the nav is open).
      this.overlay.addEventListener('click', this.closeHandler);
      this.nav.addEventListener('keyup', this.closeHandler);

      if (theme.mediaMatches.md) {
        this.overlay.addEventListener('mouseenter', this.closeHandler);
      }
    } else {
      // Remove event listener added on nav opening.
      this.overlay.removeEventListener('click', this.closeHandler);
      this.nav.removeEventListener('keyup', this.closeHandler);

      if (theme.mediaMatches.md) {
        this.overlay.removeEventListener('mouseenter', this.closeHandler);
      }
    }
  }

  /**
   * Closes the main nav menu
   */
  closeMainDisclosure() {
    if (this.mainDisclosure.classList.contains('is-open')) {
      this.close(this.mainDisclosure, true);
      this.toggleOverlay(false);
      this.childNavOpen = false;
    }
  }

  /**
   * Updates open/opening classes for disclosure element.
   * @param {Element} el - Disclosure element.
   * @param {boolean} [mainMenuOpen=true] - Main menu is open.
   */
  static open(el, mainMenuOpen = true) {
    el.open = true;

    // Update aria-expanded on the trigger summary
    const trigger = el.querySelector('summary');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');

    // Cap the max width of grandchildren on desktop their contents don't widen the dropdown
    if (theme.mediaMatches.md && !el.classList.contains('js-mega-nav')) {
      // If the nav menu spills off the right of the screen, shift it to the left
      const dropdownContainer = el.querySelector('.main-nav__child');
      if (dropdownContainer.getBoundingClientRect().right > window.innerWidth) {
        dropdownContainer.classList.add('main-nav__child--offset-right');
      }

      const dropdown = el.querySelector('.child-nav--dropdown');
      if (dropdown) {
        dropdown.querySelectorAll('.main-nav__grandchild').forEach((grandchildElem) => {
          grandchildElem.style.maxWidth = `${dropdown.clientWidth}px`;
        });
      }
    } else if (theme.mediaMatches.md && el.querySelector('.mega-nav--sidebar')) {
      const firstSummaryElem = el.querySelector('.mega-nav--sidebar details[open] summary');
      if (firstSummaryElem) {
        // Open the first sidebar mega menu
        MainMenu.handleSidenavMenuToggle(null, firstSummaryElem);
      }
    }

    // Slight delay required before starting transitions.
    setTimeout(() => {
      el.classList.remove('is-closing');
      el.classList.add('is-open');
    });

    if (mainMenuOpen) {
      removeTrapFocus();

      if (!theme.mediaMatches.md) {
        // Mobile menu: keep focus trapping (it IS a modal-like overlay)
        trapFocus(el);
        if (el.classList.contains('main-menu__disclosure')) {
          document.body.classList.add('overflow-hidden');
        }
      }
    }
  }

  /**
   * Updates close/closing classes of a disclosure element.
   * @param {Element} el - Disclosure element.
   * @param {boolean} [transition=false] - Close action has a CSS transition.
   */
  close(el, transition = true) {
    el.classList.remove('is-open');

    // Update aria-expanded on the trigger summary
    const trigger = el.querySelector('summary');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');

    if (transition) {
      el.classList.add('is-closing');

      // Safety net: if transitionend never fires (interrupted transition,
      // browser quirk, or transition-delay window), force-close after 700ms
      // (0.3s delay + 0.3s transition + 0.1s margin).
      clearTimeout(el._closeTimeout);
      el._closeTimeout = setTimeout(() => {
        if (el.classList.contains('is-closing')) {
          el.classList.remove('is-closing');
          el.open = false;
        }
      }, 700);
    } else {
      el.classList.remove('is-closing');
      el.open = false;

      removeTrapFocus(this.opener);
      this.opener = null;
    }

    setTimeout(() => {
      const offsetMenu = el.querySelector('.main-nav__child--offset-right');
      if (offsetMenu) offsetMenu.classList.remove('main-nav__child--offset-right');
    }, 200);

    if (!theme.mediaMatches.md && el.classList.contains('main-menu__disclosure')) {
      document.body.classList.remove('overflow-hidden');
    }
  }

  /**
   * Decide whether to show a particular mega menu
   * @param {object} evt - Event object
   */
  handleBlockSelect(evt) {
    const activeDisclosure = this.nav.querySelector(':scope > li > details.is-open');
    if (activeDisclosure) {
      this.close(activeDisclosure, false);
    }

    if (evt.target.matches('.js-mega-nav')) {
      MainMenu.open(evt.target, false);
      this.toggleOverlay(true);
    }
  }

  /**
   * Decide whether to hide a particular mega menu
   * @param {object} evt - Event object
   */
  handleBlockDeselect(evt) {
    if (evt.target.matches('.js-mega-nav')) {
      this.close(evt.target, false);
      this.toggleOverlay(false);
    }
  }
}

customElements.define('main-menu', MainMenu);

class CarouselSlider extends HTMLElement {
  constructor() {
    super();
    this.slides = this.querySelectorAll('.slider__item');
    if (this.slides.length < 2) return;
    window.initLazyScript(this, this.init.bind(this));
  }

  disconnectedCallback() {
    window.removeEventListener('on:breakpoint-change', this.breakpointChangeHandler);
  }

  init() {
    this.slider = this.querySelector('.slider');
    this.grid = this.querySelector('.slider__grid');
    this.nav = this.querySelector('.slider-nav');
    this.rtl = document.dir === 'rtl';
    this.breakpointChangeHandler = this.breakpointChangeHandler || this.handleBreakpointChange.bind(this);

    if (this.nav) {
      this.prevBtn = this.querySelector('button[name="prev"]');
      this.nextBtn = this.querySelector('button[name="next"]');
    }

    this.initLiveRegion();
    this.initCarouselAria();
    this.initSlider();
    window.addEventListener('on:breakpoint-change', this.breakpointChangeHandler);
  }

  /**
   * Creates a visually-hidden aria-live region for announcing carousel position.
   * Starts empty so no announcement is made on page load.
   */
  initLiveRegion() {
    if (this.liveRegion) return;
    this.liveRegion = document.createElement('div');
    this.liveRegion.className = 'visually-hidden';
    this.liveRegion.setAttribute('aria-live', 'polite');
    this.liveRegion.setAttribute('aria-atomic', 'true');
    this.appendChild(this.liveRegion);
  }

  /**
   * Sets WAI-ARIA carousel attributes on the carousel container.
   * Only sets role="region" when a heading is found to avoid a nameless landmark.
   */
  initCarouselAria() {
    this.setAttribute('aria-roledescription', 'carousel');

    // Find the section heading and reference it via aria-labelledby.
    var sectionEl = this.closest('.shopify-section');
    if (sectionEl) {
      var heading = sectionEl.querySelector('.section__header h2, .section__header .h4');
      if (heading) {
        if (!heading.id) heading.id = 'carousel-heading-' + this.slider.id;
        this.setAttribute('role', 'region');
        this.setAttribute('aria-labelledby', heading.id);
      }
    }
  }

  initSlider() {
    if (
      !(this.getAttribute('disable-mobile') && !window.matchMedia(theme.mediaQueries.sm).matches) &&
      !(this.getAttribute('disable-desktop') && window.matchMedia(theme.mediaQueries.sm).matches)
    ) {
      this.gridWidth = this.grid.clientWidth;

      // Distance between leading edges of adjacent slides (i.e. width of card + gap).
      this.slideSpan = this.getWindowOffset(this.slides[1]) - this.getWindowOffset(this.slides[0]);

      // Width of gap between slides.
      this.slideGap = this.slideSpan - this.slides[0].clientWidth;

      this.slidesPerPage = Math.round((this.gridWidth + this.slideGap) / this.slideSpan);
      this.slidesToScroll = theme.settings.sliderItemsPerNav === 'page' ? this.slidesPerPage : 1;
      this.totalPages = this.slides.length - this.slidesPerPage + 1;

      this.setCarouselState(this.totalPages > 1);
      if (this.totalPages < 2) return;

      this.sliderStart = this.getWindowOffset(this.slider);
      if (!this.sliderStart) this.sliderStart = (this.slider.clientWidth - this.gridWidth) / 2;
      this.sliderEnd = this.sliderStart + this.gridWidth;

      if (window.matchMedia('(pointer: fine)').matches) {
        this.slider.classList.add('is-grabbable');
      }

      // Set aria-live="off" on the slide container (not auto-rotating).
      this.grid.setAttribute('aria-atomic', 'false');
      this.grid.setAttribute('aria-live', 'off');

      this.initSlideAria();
      this.addListeners();
      this.updateSlideVisibility();

      if (this.nav) this.setButtonStates();
    } else {
      this.setAttribute('inactive', '');
    }
  }

  addListeners() {
    this.scrollHandler = debounce(this.handleScroll.bind(this));
    this.slider.addEventListener('scroll', this.scrollHandler);

    if (this.nav) {
      this.navClickHandler = this.handleNavClick.bind(this);
      this.nav.addEventListener('click', this.navClickHandler);
    }

    if (window.matchMedia('(pointer: fine)').matches) {
      this.mousedownHandler = this.handleMousedown.bind(this);
      this.mouseupHandler = this.handleMouseup.bind(this);
      this.mousemoveHandler = this.handleMousemove.bind(this);

      this.slider.addEventListener('mousedown', this.mousedownHandler);
      this.slider.addEventListener('mouseup', this.mouseupHandler);
      this.slider.addEventListener('mouseleave', this.mouseupHandler);
      this.slider.addEventListener('mousemove', this.mousemoveHandler);
    }
  }

  removeListeners() {
    this.slider.removeEventListener('scroll', this.scrollHandler);

    if (this.nav && this.navClickHandler) {
      this.nav.removeEventListener('click', this.navClickHandler);
    }

    this.slider.removeEventListener('mousedown', this.mousedownHandler);
    this.slider.removeEventListener('mouseup', this.mouseupHandler);
    this.slider.removeEventListener('mouseleave', this.mouseupHandler);
    this.slider.removeEventListener('mousemove', this.mousemoveHandler);
  }

  /**
   * Sets WAI-ARIA slide attributes on each slide item.
   */
  initSlideAria() {
    var total = this.slides.length;
    this.slides.forEach(function (slide, index) {
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-roledescription', 'slide');
      if (theme.strings.slideLabel) {
        slide.setAttribute(
          'aria-label',
          theme.strings.slideLabel.replace('[current]', index + 1).replace('[total]', total),
        );
      }
    });
  }

  /**
   * Updates aria-hidden on slides based on their visibility.
   * Preserves aria-hidden="false" on slides containing the active focus
   * to prevent screen readers from losing track of the focused element.
   */
  updateSlideVisibility() {
    var activeEl = document.activeElement;
    this.slides.forEach(
      function (slide) {
        var visible = this.getSlideVisibility(slide);

        if (activeEl && slide.contains(activeEl) && !visible) {
          slide.setAttribute('aria-hidden', 'false');
          return;
        }

        slide.setAttribute('aria-hidden', visible ? 'false' : 'true');
      }.bind(this),
    );
  }

  /**
   * Announces the current carousel position to screen readers via the live region.
   */
  announcePosition() {
    if (!this.liveRegion || !theme.strings.carouselStatus) return;

    var first = this.currentIndex + 1;
    var last = Math.min(this.currentIndex + this.slidesPerPage, this.slides.length);
    var total = this.slides.length;

    this.liveRegion.textContent = theme.strings.carouselStatus
      .replace('[first]', first)
      .replace('[last]', last)
      .replace('[total]', total);
  }

  /**
   * Handles 'scroll' events on the slider element.
   */
  handleScroll() {
    this.currentIndex = Math.round(this.slider.scrollLeft / this.slideSpan);
    if (this.nav) this.setButtonStates();
    this.updateSlideVisibility();
    this.announcePosition();
  }

  /**
   * Handles 'mousedown' events on the slider element.
   * @param {object} evt - Event object.
   */
  handleMousedown(evt) {
    this.mousedown = true;
    this.startX = evt.pageX - this.sliderStart;
    this.scrollPos = this.slider.scrollLeft;
    this.slider.classList.add('is-grabbing');
  }

  /**
   * Handles 'mouseup' events on the slider element.
   */
  handleMouseup() {
    this.mousedown = false;
    this.slider.classList.remove('is-grabbing');
  }

  /**
   * Handles 'mousemove' events on the slider element.
   * @param {object} evt - Event object.
   */
  handleMousemove(evt) {
    if (!this.mousedown) return;
    evt.preventDefault();

    const x = evt.pageX - this.sliderStart;
    this.slider.scrollLeft = this.scrollPos - (x - this.startX) * 2;
  }

  /**
   * Handles 'click' events on the nav buttons container.
   * @param {object} evt - Event object.
   */
  handleNavClick(evt) {
    if (!evt.target.matches('.slider-nav__btn')) return;

    if ((evt.target.name === 'next' && !this.rtl) || (evt.target.name === 'prev' && this.rtl)) {
      this.scrollPos = this.slider.scrollLeft + this.slidesToScroll * this.slideSpan;
    } else {
      this.scrollPos = this.slider.scrollLeft - this.slidesToScroll * this.slideSpan;
    }

    this.slider.scrollTo({ left: this.scrollPos, behavior: 'smooth' });
  }

  /**
   * Handles 'on:breakpoint-change' events on the window.
   */
  handleBreakpointChange() {
    this.removeListeners();
    this.initSlider();
  }

  /**
   * Gets the offset of an element from the edge of the viewport (left for ltr, right for rtl).
   * @param {number} el - Element.
   * @returns {number}
   */
  getWindowOffset(el) {
    return this.rtl ? window.innerWidth - el.getBoundingClientRect().right : el.getBoundingClientRect().left;
  }

  /**
   * Gets the visible state of a slide.
   * @param {Element} el - Slide element.
   * @returns {boolean}
   */
  getSlideVisibility(el) {
    const slideStart = this.getWindowOffset(el);
    const slideEnd = Math.floor(slideStart + this.slides[0].clientWidth);
    return slideStart >= this.sliderStart && slideEnd <= this.sliderEnd;
  }

  /**
   * Sets the active state of the carousel.
   * @param {boolean} active - Set carousel as active.
   */
  setCarouselState(active) {
    if (active) {
      this.removeAttribute('inactive');

      // If slider width changed when activated, reinitialise it.
      if (this.gridWidth !== this.grid.clientWidth) {
        this.handleBreakpointChange();
      }
    } else {
      this.setAttribute('inactive', '');
    }
  }

  /**
   * Sets the disabled state of the nav buttons.
   */
  setButtonStates() {
    if (!this.prevBtn && !this.nextBtn) {
      return;
    }

    this.prevBtn.disabled = this.getSlideVisibility(this.slides[0]) && this.slider.scrollLeft === 0;
    this.nextBtn.disabled = this.getSlideVisibility(this.slides[this.slides.length - 1]);
  }
}

customElements.define('carousel-slider', CarouselSlider);

class ProductRecommendations extends HTMLElement {
  constructor() {
    super();
    window.initLazyScript(this, this.init.bind(this), 500);
  }

  async init() {
    const { productId } = this.dataset;
    if (!productId) return;

    try {
      const response = await fetch(`${this.dataset.url}&product_id=${productId}`);
      if (!response.ok) throw new Error(response.status);

      const tmpl = document.createElement('template');
      tmpl.innerHTML = await response.text();

      const el = tmpl.content.querySelector('product-recommendations');
      if (el && el.hasChildNodes()) {
        this.innerHTML = el.innerHTML;
      }

      window.initLazyImages();
    } catch (error) {
      console.log(error); // eslint-disable-line
    }
  }
}

customElements.define('product-recommendations', ProductRecommendations);

setTimeout(() => {
  requestAnimationFrame(initLazyImages);
}, 0);
window.initLazyScript = initLazyScript;

document.addEventListener('keydown', (evt) => {
  if (evt.code === 'Tab') {
    document.body.classList.add('tab-used');
  }
});

document.addEventListener('DOMContentLoaded', () => {
  setTimeout(() => {
    document.body.classList.add('dom-loaded');
  }, 0);

  setTimeout(() => {
    document.body.classList.add('dom-loaded-plus-6');
  }, 6000);

  if (theme.settings.externalLinksNewTab) {
    document.addEventListener('click', (evt) => {
      const link = evt.target.tagName === 'A' ? evt.target : evt.target.closest('a');
      if (link && link.tagName === 'A' && window.location.hostname !== new URL(link.href).hostname) {
        link.target = '_blank';
      }
    });
  }

  // Ensure anchor scrolling is smooth (this shouldn't be added in the CSS)
  document.addEventListener('click', (evt) => {
    if (
      evt.target.tagName === 'A' &&
      window.location.hostname === new URL(evt.target.href).hostname &&
      evt.target.href.includes('#')
    ) {
      document.getElementsByTagName('html')[0].style.scrollBehavior = 'smooth';
      setTimeout(() => {
        document.getElementsByTagName('html')[0].style.scrollBehavior = '';
      }, 1000);
    }
  });
});

// Emit an event to indicate the page was restored from bfcache, https://web.dev/articles/bfcache
window.addEventListener('pageshow', (evt) => {
  if (evt.persisted) {
    document.dispatchEvent(new CustomEvent('on:bfcache:load-restore'));
  } else {
    document.dispatchEvent(new CustomEvent('on:bfcache:load-normal'));
  }
});
